import T from "@/components/forms/TextField.module.css";

import { WButton } from "@/components/button/WButton.tsx";
import { ariaTanstackFieldProps } from "@/components/forms/ariaTanstackFieldProps.tsx";
import { FormFieldErrors } from "@/components/forms/FormFieldErrors.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import {
    addForwardingRuleToLoadBalancerMutation,
    type LoadBalancerWithType,
} from "@/modules/network/loadbalancer/loadBalancerApi.ts";
import { validatePort, validateSourcePort } from "@/modules/network/loadbalancer/validatePorts.ts";
import { Trans } from "@lingui/react/macro";
import { useForm } from "@tanstack/react-form";
import { Input, NumberField } from "react-aria-components";

interface AddForwardingRuleFormProps {
    item: LoadBalancerWithType;
    onClose: () => void;
}

export function AddForwardingRuleForm({ item, onClose }: AddForwardingRuleFormProps) {
    const addForwardingRuleMutation = useStandardMutation(addForwardingRuleToLoadBalancerMutation);

    const { location, uuid } = item;

    const form = useForm({
        defaultValues: {
            source_port: NaN,
            target_port: NaN,
        },
        async onSubmit({ value }) {
            await addForwardingRuleMutation.mutateAsync({
                location,
                uuid,
                body: value,
            });
            onClose();
        },
    });

    return (
        <>
            <td>TCP</td>
            <td className="text-right">
                <form.Field
                    name="source_port"
                    validators={{
                        onBlur: ({ value }) => validateSourcePort(value, item.forwarding_rules, false),
                    }}
                >
                    {(api) => (
                        <NumberField
                            autoFocus
                            className={T.NumberField}
                            aria-label="Set source port value"
                            isRequired
                            formatOptions={{
                                useGrouping: false,
                            }}
                            {...ariaTanstackFieldProps(api)}
                        >
                            <div className="w-7.5rem ml-auto whitespace-pre-wrap">
                                <Input className={T.Input} />
                                <FormFieldErrors errors={api.state.meta.errors} />
                            </div>
                        </NumberField>
                    )}
                </form.Field>
            </td>
            <td className="text-center">
                <MaskIcon className="jp-arrow-thin-right-icon size-1.25rem color-muted" />
            </td>
            <td>
                <form.Field
                    name="target_port"
                    validators={{
                        onBlur: ({ value }) => validatePort(value),
                    }}
                >
                    {(api) => (
                        <NumberField
                            className={T.NumberField}
                            aria-label="Set target port value"
                            isRequired
                            formatOptions={{
                                useGrouping: false,
                            }}
                            {...ariaTanstackFieldProps(api)}
                        >
                            <div className="w-7.5rem whitespace-pre-wrap">
                                <Input className={T.Input} />
                                <FormFieldErrors errors={api.state.meta.errors} />
                            </div>
                        </NumberField>
                    )}
                </form.Field>
            </td>
            <td className="text-right">
                <div className="flex gap-0.5em justify-end">
                    <WButton color="primary" icon="jp-icon-add" action={() => form.handleSubmit()}>
                        <Trans>Add</Trans>
                    </WButton>
                    <WButton action={onClose}>
                        <Trans>Close</Trans>
                    </WButton>
                </div>
            </td>
        </>
    );
}
